import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { api } from '../../utils/api';
import { Plus, Edit, Trash2, Loader2, Check, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const CATEGORY_OPTIONS = [
  { value: 'broadband_only', label: 'Velocity Series - Broadband Only' },
  { value: 'broadband_iptv', label: 'Vista Series - Broadband + IPTV' },
  { value: 'broadband_ott', label: 'Pulse Series - Broadband + OTT' },
  { value: 'broadband_iptv_ott', label: 'Infinity Series - Complete Package' }
];

export const AdminPlans = () => {
  const { toast } = useToast();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'broadband_only',
    speed_mbps: '',
    yearly_price: '',
    data_label: 'Unlimited',
    features: [],
    is_active: true
  });
  const [featureInput, setFeatureInput] = useState('');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const data = await api.adminGetPlans(true);
      setPlans(data);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load plans',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        name: plan.name,
        category: plan.category,
        speed_mbps: plan.speed_mbps.toString(),
        yearly_price: plan.yearly_price.toString(),
        data_label: plan.data_label || 'Unlimited',
        features: plan.features || [],
        is_active: plan.is_active
      });
    } else {
      setEditingPlan(null);
      setFormData({
        name: '',
        category: 'broadband_only',
        speed_mbps: '',
        yearly_price: '',
        data_label: 'Unlimited',
        features: [],
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleAddFeature = () => {
    if (featureInput.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, featureInput.trim()]
      });
      setFeatureInput('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const planData = {
      ...formData,
      speed_mbps: parseInt(formData.speed_mbps),
      yearly_price: parseFloat(formData.yearly_price)
    };

    try {
      if (editingPlan) {
        await api.adminUpdatePlan(editingPlan.id, planData);
        toast({
          title: 'Success',
          description: 'Plan updated successfully'
        });
      } else {
        await api.adminCreatePlan(planData);
        toast({
          title: 'Success',
          description: 'Plan created successfully'
        });
      }
      setDialogOpen(false);
      fetchPlans();
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to save plan',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (planId) => {
    if (!confirm('Are you sure you want to deactivate this plan?')) return;

    try {
      await api.adminDeletePlan(planId);
      toast({
        title: 'Success',
        description: 'Plan deactivated successfully'
      });
      fetchPlans();
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to deactivate plan',
        variant: 'destructive'
      });
    }
  };

  const groupedPlans = plans.reduce((acc, plan) => {
    if (!acc[plan.category]) {
      acc[plan.category] = [];
    }
    acc[plan.category].push(plan);
    return acc;
  }, {});

  if (loading) {
    return (
      // eslint-disable-next-line
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Plans Management</h1>
          <p className="text-gray-600 mt-1">Manage broadband plans and pricing</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-red-600 hover:bg-red-700"
            >
              <Plus size={20} className="mr-2" />
              Add New Plan
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingPlan ? 'Edit Plan' : 'Create New Plan'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Plan Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., Velocity Start"
                  />
                </div>
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <select
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-gray-300"
                    required
                  >
                    {CATEGORY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="speed_mbps">Speed (Mbps) *</Label>
                  <Input
                    id="speed_mbps"
                    type="number"
                    value={formData.speed_mbps}
                    onChange={(e) => setFormData({ ...formData, speed_mbps: e.target.value })}
                    required
                    placeholder="e.g., 100"
                  />
                </div>
                <div>
                  <Label htmlFor="yearly_price">Yearly Price (Rs) *</Label>
                  <Input
                    id="yearly_price"
                    type="number"
                    step="0.01"
                    value={formData.yearly_price}
                    onChange={(e) => setFormData({ ...formData, yearly_price: e.target.value })}
                    required
                    placeholder="e.g., 9588"
                  />
                  {formData.yearly_price && (
                    <p className="text-xs text-gray-500 mt-1">
                      Monthly: Rs.{(parseFloat(formData.yearly_price) / 12).toFixed(2)}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="data_label">Data Label</Label>
                <Input
                  id="data_label"
                  value={formData.data_label}
                  onChange={(e) => setFormData({ ...formData, data_label: e.target.value })}
                  placeholder="e.g., Unlimited"
                />
              </div>

              <div>
                <Label>Features</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    placeholder="Add a feature..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddFeature();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={handleAddFeature}
                    variant="outline"
                  >
                    Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.features.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-50 p-2 rounded"
                    >
                      <span className="text-sm">{feature}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFeature(index)}
                      >
                        <X size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="is_active">Active (visible to customers)</Label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  {editingPlan ? 'Update Plan' : 'Create Plan'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Alert>
        <AlertDescription>
          [Note] All prices are yearly. Monthly prices are automatically calculated (yearly/12).
          Plans marked as inactive won't be visible to customers.
        </AlertDescription>
      </Alert>

      {/* Plans by Category */}
      <div className="space-y-6">
        {CATEGORY_OPTIONS.map((category) => {
          const categoryPlans = groupedPlans[category.value] || [];
          if (categoryPlans.length === 0) return null;

          return (
            <Card key={category.value}>
              <CardHeader>
                <CardTitle>{category.label}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {categoryPlans.map((plan) => (
                    <Card
                      key={plan.id}
                      className={`${!plan.is_active ? 'opacity-50 bg-gray-50' : ''}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="font-bold text-lg">{plan.name}</h3>
                            <p className="text-sm text-gray-600">{plan.speed_mbps} Mbps</p>
                          </div>
                          {!plan.is_active && (
                            <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs">
                              Inactive
                            </span>
                          )}
                        </div>
                        <div className="text-2xl font-bold text-red-600 mb-2">
                          Rs. {plan.yearly_price.toLocaleString()}/yr
                        </div>
                        <p className="text-sm text-gray-500 mb-4">
                          Rs. {plan.monthly_price}/month
                        </p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenDialog(plan)}
                            className="flex-1"
                          >
                            <Edit size={16} className="mr-1" />
                            Edit
                          </Button>
                          {plan.is_active && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(plan.id)}
                              className="text-red-600 border-red-600"
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {plans.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">No plans created yet. Click "Add New Plan" to get started.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
